import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import {
  computeRecovery,
  exertionZone,
  MIN_COMPONENTS_FOR_SCORE,
  type DataQuality,
  type IllnessSignals,
  type RecoveryResult,
  type ExertionZone,
} from "@/lib/recovery/formula";

/**
 * Reads today's precomputed Recovery Score from `daily_recovery`. v2 (BW18.5
 * PR-5.B) uses ln(rmssd) 7-day baselines, sleep debt, training load
 * acute/chronic ratio, EA min(yesterday, 3d), and a daily check-in
 * (subjective + illness flags). Falls back to client-side computation
 * against the last 28 days of vitals when the cron hasn't run yet.
 */
export type TodayRecovery = {
  score: number | null;
  components: RecoveryResult | null;
  zone: ExertionZone | null;
  source: "cron" | "client" | "none";
  computed_at: string | null;
  data_quality: DataQuality | null;
  available_components: number;
};

const todayIso = (): string => new Date().toISOString().slice(0, 10);

export function useTodayRecovery() {
  const { session } = useSession();
  const userId = session?.user?.id;

  return useQuery<TodayRecovery>({
    queryKey: ["today_recovery", userId, todayIso()],
    enabled: Boolean(userId),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      if (!userId) {
        return {
          score: null,
          components: null,
          zone: null,
          source: "none",
          computed_at: null,
          data_quality: null,
          available_components: 0,
        };
      }

      // 1. Try precomputed.
      const { data: precomputed } = await supabase
        .from("daily_recovery")
        .select(
          "score, hrv_z_component, sleep_component, rhr_z_component, exertion_residual, ea_component, ea_kcal_per_kg_ffm, ea_status, computed_at, formula_version",
        )
        .eq("user_id", userId)
        .eq("date", todayIso())
        .maybeSingle();

      if (precomputed) {
        const availableCount = countAvailable([
          precomputed.hrv_z_component,
          precomputed.sleep_component,
          precomputed.rhr_z_component,
          precomputed.exertion_residual,
          precomputed.ea_component,
        ]);
        const dataQuality: DataQuality =
          availableCount >= MIN_COMPONENTS_FOR_SCORE
            ? availableCount >= 4
              ? "sufficient"
              : "partial"
            : "insufficient";
        // Worker still writes the v1 column shape (exertion_residual). Map
        // it onto v2's load_component for display until the cron mirror
        // upgrades.
        const components: RecoveryResult = {
          score: precomputed.score,
          hrv_z_component: precomputed.hrv_z_component,
          sleep_component: precomputed.sleep_component,
          rhr_z_component: precomputed.rhr_z_component,
          load_component: precomputed.exertion_residual,
          ea_component: precomputed.ea_component,
          subjective_component: null,
          ea_kcal_per_kg_ffm: precomputed.ea_kcal_per_kg_ffm,
          ea_status: (precomputed.ea_status as RecoveryResult["ea_status"]) ?? null,
          override_reason: null,
          formula_version: "v2",
          available_components: availableCount,
          data_quality: dataQuality,
        };
        const renderedScore =
          dataQuality === "insufficient" ? null : precomputed.score;
        return {
          score: renderedScore,
          components,
          zone: renderedScore != null ? exertionZone(renderedScore) : null,
          source: "cron",
          computed_at: precomputed.computed_at,
          data_quality: dataQuality,
          available_components: availableCount,
        };
      }

      // 2. Client fallback. Pull 28 days of vitals, last few sleeps,
      //    yesterday's exertion, last 3 days nutrition, and today's
      //    daily_check_in (if any).
      const since = new Date();
      since.setDate(since.getDate() - 28);
      const sinceIso = since.toISOString().slice(0, 10);

      const [vitalsRes, sleepsRes, profileRes, bodyRes, nutritionRes, checkInRes] =
        await Promise.all([
          supabase
            .from("daily_vitals")
            .select("date, hrv_status_ms, rhr_bpm, training_load_7d")
            .eq("user_id", userId)
            .gte("date", sinceIso)
            .order("date", { ascending: false }),
          supabase
            .from("sleep_record")
            .select("date, duration_min")
            .eq("user_id", userId)
            .order("date", { ascending: false })
            .limit(7),
          supabase
            .from("athlete_profile")
            .select("current_weight_kg, current_body_fat_pct")
            .eq("user_id", userId)
            .maybeSingle(),
          supabase
            .from("body_measurement")
            .select("muscle_mass_kg, body_fat_pct")
            .eq("user_id", userId)
            .order("ts", { ascending: false })
            .limit(1)
            .maybeSingle(),
          supabase
            .from("nutrition_roll")
            .select("date, kcal_total")
            .eq("user_id", userId)
            .order("date", { ascending: false })
            .limit(4),
          supabase
            .from("daily_check_in")
            .select(
              "date, subjective_readiness, subjective_soreness, elevated_resp_rate, elevated_wrist_temp, fever_or_sore_throat",
            )
            .eq("user_id", userId)
            .eq("date", todayIso())
            .maybeSingle(),
        ]);

      const vitals = vitalsRes.data ?? [];
      const sleeps = sleepsRes.data ?? [];
      const lastNightVitals = vitals[0];
      const baselineHRV = vitals
        .map((v) => v.hrv_status_ms)
        .filter((v): v is number => typeof v === "number");
      const baselineRHR = vitals
        .map((v) => v.rhr_bpm)
        .filter((v): v is number => typeof v === "number");

      const sleepHoursLastNight = sleeps[0]?.duration_min
        ? sleeps[0].duration_min / 60
        : null;
      const sleepHoursList = sleeps
        .map((s) => (s.duration_min != null ? s.duration_min / 60 : null))
        .filter((v): v is number => typeof v === "number");
      const sleep7dAvg =
        sleepHoursList.length > 0
          ? sleepHoursList.reduce((a, b) => a + b, 0) / sleepHoursList.length
          : null;
      const sleepDebt7d =
        sleep7dAvg != null
          ? Math.max(0, sleepHoursList.length * 7.5 - sleepHoursList.reduce((a, b) => a + b, 0))
          : null;

      const weight = profileRes.data?.current_weight_kg ?? null;
      const bodyFatPct =
        bodyRes.data?.body_fat_pct ?? profileRes.data?.current_body_fat_pct ?? null;
      let ffm: number | null = bodyRes.data?.muscle_mass_kg ?? null;
      if (ffm == null && weight != null && bodyFatPct != null) {
        ffm = weight * (1 - bodyFatPct / 100);
      }

      const nutritionRows = nutritionRes.data ?? [];
      const yesterdayKcal = nutritionRows[1]?.kcal_total ?? null;
      const last3Kcal = nutritionRows
        .slice(0, 3)
        .map((r) => Number(r.kcal_total))
        .filter((v) => Number.isFinite(v));
      const ea3dAvg =
        last3Kcal.length > 0 && ffm && ffm > 0
          ? last3Kcal.reduce((a, b) => a + b, 0) / (last3Kcal.length * ffm)
          : null;

      const checkIn = checkInRes.data ?? null;
      const illnessSignals: IllnessSignals | null = checkIn
        ? {
            elevated_resp_rate: Boolean(checkIn.elevated_resp_rate),
            elevated_wrist_temp: Boolean(checkIn.elevated_wrist_temp),
            fever_or_sore_throat: Boolean(checkIn.fever_or_sore_throat),
          }
        : null;

      const components = computeRecovery({
        hrv_7d_ln_rmssd: null,
        hrv_baseline_mean: null,
        hrv_baseline_sd: null,
        hrv_last_night_ms: lastNightVitals?.hrv_status_ms ?? null,
        hrv_baseline_samples: baselineHRV,
        sleep_hours: sleepHoursLastNight,
        sleep_target_hours: 7.5,
        sleep_7d_avg_h: sleep7dAvg,
        sleep_debt_7d_h: sleepDebt7d,
        rhr_7d_bpm: null,
        rhr_baseline_mean: null,
        rhr_baseline_sd: null,
        rhr_last_night_bpm: lastNightVitals?.rhr_bpm ?? null,
        rhr_baseline_samples: baselineRHR,
        training_load_7d: lastNightVitals?.training_load_7d ?? null,
        training_load_28d_avg: avg(
          vitals
            .map((v) => v.training_load_7d)
            .filter((v): v is number => typeof v === "number"),
        ),
        kcal_in_yesterday: yesterdayKcal,
        kcal_workout_yesterday: 0,
        ea_3d_avg_kcal_per_kg_ffm: ea3dAvg,
        fat_free_mass_kg: ffm,
        subjective_readiness: checkIn?.subjective_readiness ?? null,
        subjective_soreness: checkIn?.subjective_soreness ?? null,
        illness_signals: illnessSignals,
      });

      const renderedScore =
        components.data_quality === "insufficient" ? null : components.score;
      return {
        score: renderedScore,
        components,
        zone: renderedScore != null ? exertionZone(renderedScore) : null,
        source: "client",
        computed_at: null,
        data_quality: components.data_quality,
        available_components: components.available_components,
      };
    },
  });
}

function countAvailable(values: Array<number | null | undefined>): number {
  return values.reduce<number>((n, v) => n + (typeof v === "number" ? 1 : 0), 0);
}

function avg(xs: number[]): number | null {
  if (xs.length === 0) return null;
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}
